import React from 'react'
import T from 'prop-types'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import axios from 'axios'
import config from 'config'
import { Form, Formik } from 'formik'
import logger from 'logger'
import * as Yup from 'yup'

import useSnackbar from 'components/organisms/Snackbar/useSnackbar'

import { defaultLanguage } from '../../../i18n'

import UserInfoFields from './UserInfoFields'

const COMMON_REQUIRED_FIELD = 'common.requiredField'

const UserInfoDialog = ({ user, open, handleClose, refetch, allowedRoles }) => {
  const { t } = useTranslation()
  const { showSnackbarMessage } = useSnackbar()

  const UserSchema = Yup.object().shape({
    firstName: Yup.string().required(t(COMMON_REQUIRED_FIELD)),
    lastName: Yup.string().required(t(COMMON_REQUIRED_FIELD)),
    accountType: Yup.string().required(t(COMMON_REQUIRED_FIELD)),
    partnerId: Yup.string(),
    status: Yup.string().required(t(COMMON_REQUIRED_FIELD)),
    email: Yup.string()
      .email('Invalid email')
      .required(t(COMMON_REQUIRED_FIELD)),
    language: Yup.string().required(t(COMMON_REQUIRED_FIELD)),
    receiveEmailDisabled: Yup.string().required(t(COMMON_REQUIRED_FIELD)),
  })

  if (!open) return null

  const onSubmit = async (values) => {
    const data = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      partnerId: values.partnerId,
      accountType: values.accountType,
      state: values.status,
      language: values.language,
      receiveEmailDisabled: values.receiveEmailDisabled,
    }

    if (!user) {
      try {
        await axios.post(`${config.apiBaseUrl}users`, data)

        showSnackbarMessage({
          severity: 'success',
          message: t('users.create.success'),
        })
      } catch (e) {
        logger.error('[Error] - partner creation', e)
        showSnackbarMessage({
          severity: 'error',
          message: t('users.create.error'),
        })
      }
    } else {
      try {
        await axios.patch(`${config.apiBaseUrl}users/${user.userId}`, data)

        showSnackbarMessage({
          severity: 'success',
          message: t('users.edit.success'),
        })
      } catch (e) {
        logger.error('[Error] - partner editing', e)
        showSnackbarMessage({
          severity: 'error',
          message: t('users.edit.error'),
        })
      }
    }

    refetch()
    handleClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <Formik
        validationSchema={UserSchema}
        enableReinitialize={true}
        initialValues={{
          firstName: user?.firstName ?? '',
          lastName: user?.lastName ?? '',
          email: user?.email ?? '',
          partnerId: user?.partner?.partnerId ?? '',
          accountType: user?.accountType ?? '',
          status: user?.state ?? '',
          language: user?.language ?? defaultLanguage,
          receiveEmailDisabled: user?.receiveEmailDisabled ?? false,
        }}
        onSubmit={onSubmit}
      >
        <Form>
          <DialogTitle>
            {t(
              user
                ? 'users.userInfoDialog.title2'
                : 'users.userInfoDialog.title1'
            )}
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <UserInfoFields allowedRoles={allowedRoles} />
          <DialogActions>
            <Button
              variant="text"
              onClick={handleClose}
              data-testid={'user-dialog-cancel-button'}
            >
              {t('users.userInfoDialog.cancelButton')}
            </Button>
            <Button
              variant="contained"
              type="submit"
              data-testid={'user-dialog-submit-button'}
            >
              {t(
                user
                  ? 'users.userInfoDialog.confirmButton2'
                  : 'users.userInfoDialog.confirmButton1'
              )}
            </Button>
          </DialogActions>
        </Form>
      </Formik>
    </Dialog>
  )
}

UserInfoDialog.propTypes = {
  user: T.any, // TODO
  open: T.bool,
  handleClose: T.func,
  refetch: T.func,
}

export default UserInfoDialog
